/**
 * Session Manager Utility
 * Handles 2-day session expiration and login timestamp management
 */

const SESSION_TIMEOUT_MS = 48 * 60 * 60 * 1000; // 2 days = 172800000 ms
const LOGIN_TIMESTAMP_KEY = "loginTimestamp";
const SESSION_DATA_KEYS = ["token", "persist:root"];
const FORCE_LOGOUT_KEY = "forceLogoutVersion";

// bump this value whenever you need to force everyone offline
const CURRENT_FORCE_LOGOUT_VERSION = "1.1"; // increment to logout all users


/**
 * Store the login timestamp when user logs in
 */
export const setLoginTimestamp = () => {
  try {
    const timestamp = Date.now();
    localStorage.setItem(LOGIN_TIMESTAMP_KEY, timestamp.toString());
    console.log("Login timestamp set:", new Date(timestamp).toLocaleString());
  } catch (err) {
    console.error("Error setting login timestamp:", err);
  }
};

/**
 * Get the login timestamp from localStorage
 */
export const getLoginTimestamp = () => {
  try {
    const timestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp) : null;
  } catch (err) {
    console.error("Error getting login timestamp:", err);
    return null;
  }
};

/**
 * Check if session has expired (more than 2 days old)
 */
export const isSessionExpired = () => {
  const loginTimestamp = getLoginTimestamp();
  
  if (!loginTimestamp) {
    return false; // No session data, not expired
  }
  
  const currentTime = Date.now();
  const elapsedTime = currentTime - loginTimestamp;
  const isExpired = elapsedTime > SESSION_TIMEOUT_MS;
  
  if (isExpired) {
    console.warn("Session expired! Elapsed time:", Math.floor(elapsedTime / 1000 / 60 / 60), "hours");
  }
  
  return isExpired;
};

/**
 * Get remaining session time in milliseconds
 */
export const getRemainingSessionTime = () => {
  const loginTimestamp = getLoginTimestamp();
  
  if (!loginTimestamp) {
    return null;
  }
  
  const currentTime = Date.now();
  const elapsedTime = currentTime - loginTimestamp;
  const remainingTime = SESSION_TIMEOUT_MS - elapsedTime;
  
  return Math.max(0, remainingTime);
};

/**
 * Format remaining time to readable string
 */
export const formatRemainingTime = () => {
  const remaining = getRemainingSessionTime();
  
  if (!remaining) {
    return "No active session";
  }
  
  if (remaining <= 0) {
    return "Session expired";
  }
  
  const hours = Math.floor(remaining / 1000 / 60 / 60);
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / 1000 / 60);
  
  return `${hours}h ${minutes}m remaining`;
};

/**
 * Clear all session data from localStorage
 */
export const clearSessionData = () => {
  try {
    SESSION_DATA_KEYS.forEach(key => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
    console.log("Session data cleared from localStorage");
  } catch (err) {
    console.error("Error clearing session data:", err);
  }
};

/**
 * Logout user - clear session and redirect to login
 */
export const logoutUser = (redirect = true) => {
  try {
    clearSessionData();
    console.log("User logged out due to session expiration");
    
    if (redirect && window.location.pathname !== "/page/login") {
      // Redirect to login page
      window.location.href = "/page/login";
    }
  } catch (err) {
    console.error("Error logging out user:", err);
  }
};

/**
 * Check session on app initialization
 * This should be called when the app loads
 */
// determines whether we should immediately logout because the
// "force logout" version stored in localStorage is out of date.  When
// we bump CURRENT_FORCE_LOGOUT_VERSION, every client that runs this
// function will compare and, if different, store the new value and
// return true so the caller can log the user out.
const shouldForceLogout = () => {
  try {
    const stored = localStorage.getItem(FORCE_LOGOUT_KEY);
    if (stored !== CURRENT_FORCE_LOGOUT_VERSION) {
      localStorage.setItem(FORCE_LOGOUT_KEY, CURRENT_FORCE_LOGOUT_VERSION);
      console.warn("Force logout triggered (version mismatch)");
      return true;
    }
  } catch (err) {
    console.error("Error checking force logout version", err);
  }
  return false;
};

export const checkSessionExpiration = () => {
  // if we are asked to force everybody offline, just do it
  if (shouldForceLogout()) {
    logoutUser(true);
    return true;
  }

  // check for existence of a login timestamp; this covers JWT users but
  // also any other auth flow (firebase, auth0) that set the timestamp.
  const loginTimestamp = getLoginTimestamp();

  // if we have *no* timestamp at all we consider the session invalid
  // (this is the case for users who logged in before this feature was
  // introduced). we don't even care about a token value; we'll log them
  // out immediately and the app will require a fresh login.
  if (!loginTimestamp) {
    console.warn("No login timestamp found - forcing logout for existing user");
    logoutUser(true);
    return true;
  }

  // only now do we look at expiry time
  if (isSessionExpired()) {
    logoutUser(true);
    return true; // Session was expired and user was logged out
  }

  return false; // Session is still valid
};

/**
 * Setup session expiration check interval
 * Checks every 5 minutes if session has expired
 */
export const setupSessionExpirationCheck = () => {
  const checkInterval = 5 * 60 * 1000; // Check every 5 minutes
  
  const intervalId = setInterval(() => {
    checkSessionExpiration();
  }, checkInterval);
  
  return intervalId;
};
